"use client";

import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { useRouter } from "next/navigation";
import { useMemo } from "react";

/**
 * Program Cards 1045 — BDU Other Specialisations.
 *
 * Port of BDU v1 `components/Degree/OtherSpecialisations.tsx` + `organisms/CourseCards.tsx`:
 * a centred two-line heading ("Didn't find what you need?" over "Explore other
 * Specialisation Courses" with a rule under it) and a horizontally scrolling card rail.
 *
 * Distinct from Program Cards 1038 (Specialisations): the heading carries an eyebrow
 * line, the cards scroll rather than wrap from 768px up, and the card title
 * type scale differs (17px parent / 24px accent, vs 32px / 18px).
 *
 * BREAKPOINTS — v1 `md:` (768px) is `tab:` here (this app remaps `md:` to 1230px).
 */

interface CardRow {
  id?: string | number;
  meta_title?: string;
  /** Parent programme — set on specialisations, drives the two-line title */
  meta_parent_title?: string;
  meta_description?: string;
  meta_duration?: string;
  meta_image?: string;
  meta_alt_image?: string;
  meta_url?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface ProgramCards1045Data {
  metadata?: CardRow[];
  /** Eyebrow line above the heading */
  pre_title?: string;
  pre_title_color?: string;
  title?: string;
  title_text_color?: string;
  /** Rule under the heading */
  gradient_color_1?: string;
  gradient_color_2?: string;
  /** Card tint over the image */
  card_background_color?: string;
  cta_text?: string;
  status?: boolean;
}

/** Fixed in the reference: the specialisation accent colour. */
const ACCENT = "#F4F217";

/** The 16x16 arrow the reference pulls from its icon map. */
const KnowMoreArrow = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M8.42542 5.59011C8.55821 5.45727 8.63281 5.27714 8.63281 5.08931C8.63281 4.90149 8.55821 4.72135 8.42542 4.58852L4.41838 0.58148C4.35304 0.513827 4.27488 0.459865 4.18846 0.422742C4.10204 0.385619 4.00909 0.366078 3.91504 0.365261C3.82099 0.364444 3.72771 0.382366 3.64066 0.417982C3.55361 0.453597 3.47452 0.506193 3.40802 0.572701C3.34151 0.639208 3.28891 0.718294 3.2533 0.805346C3.21768 0.892398 3.19976 0.985671 3.20058 1.07972C3.20139 1.17377 3.22093 1.26672 3.25806 1.35314C3.29518 1.43956 3.34914 1.51772 3.4168 1.58306L6.21471 4.38098L-3.16574 4.38098C-3.35361 4.38098 -3.53377 4.45561 -3.66661 4.58845C-3.79945 4.72128 -3.87408 4.90145 -3.87408 5.08931C-3.87408 5.27717 -3.79945 5.45734 -3.66661 5.59018C-3.53377 5.72302 -3.35361 5.79765 -3.16574 5.79765L6.21471 5.79765L3.4168 8.59556C3.28777 8.72916 3.21637 8.90808 3.21799 9.09381C3.2196 9.27953 3.29409 9.45719 3.42543 9.58852C3.55676 9.71985 3.73442 9.79434 3.92014 9.79596C4.10586 9.79757 4.28479 9.72618 4.41838 9.59715L8.42542 5.59011Z"
      fill="white"
    />
  </svg>
);

const ProgramCards1045 = ({ data }: { data: any }) => {
  const sectionData = (data?.data ?? data) as ProgramCards1045Data | undefined;
  const router = useRouter();

  const {
    pre_title = "",
    pre_title_color = "#222023",
    title = "",
    title_text_color = "#222023",
    gradient_color_1 = "#FF0013",
    gradient_color_2 = "#990013",
    card_background_color = "#222023A1",
    cta_text = "Know more",
  } = sectionData || {};

  const cards = useMemo(() => {
    const rows = sectionData?.metadata;
    if (!Array.isArray(rows)) return [];
    return rows
      .filter((r) => r?.meta_status !== false)
      .sort(
        (a, b) =>
          (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  if (!sectionData?.status || cards.length === 0) return null;

  return (
    <div className="flex w-full flex-col items-start justify-center gap-[32px] py-10 tab:pl-[64px] tab:pr-0">
      <div className="flex flex-col items-center px-5 text-center tab:w-full tab:gap-0 tab:pl-0 tab:pr-[64px]">
        {pre_title && (
          <p
            className="text-base/[28px] font-normal"
            style={{ color: pre_title_color }}
          >
            {pre_title}
          </p>
        )}
        <h2
          className="text-[24px]/[36px] font-bold tab:text-[28px]/[36px]"
          style={{ color: title_text_color }}
        >
          {title}
        </h2>
        <div
          className="mt-2 h-[3px] w-[174px]"
          style={{
            backgroundImage: `linear-gradient(90deg, ${gradient_color_1} 0%, ${gradient_color_2} 100%)`,
          }}
        />
      </div>

      <div className="flex w-full flex-wrap justify-center gap-[12px] tab:flex-nowrap tab:justify-start tab:overflow-x-auto tab:[scrollbar-width:none] tab:[&::-webkit-scrollbar]:hidden">
        {cards.map((card, index) => {
          const parent = (card.meta_parent_title || "").trim();
          const isSpecialisation = parent.length > 0;
          const headline = isSpecialisation ? parent : card.meta_title;

          return (
            <div
              key={card.id ?? index}
              onClick={() => {
                ctaClick({
                  section: "program_cards_1045",
                  component: "know_more",
                  label: cta_text,
                  step: "",
                  entity: card.meta_title || "",
                  parentEntity: parent,
                });
                if (card.meta_url) router.push(card.meta_url);
              }}
              aria-label={card.meta_alt_image || ""}
              className={cn(
                "relative min-h-[215px] w-[323px] shrink-0 cursor-pointer overflow-hidden rounded-[14px]",
                "px-5 pb-7 pt-[22px] text-white",
                "tab:h-[263px] tab:w-[244px]",
              )}
              style={{
                background: card.meta_image
                  ? `url(${card.meta_image}) no-repeat center center`
                  : undefined,
                backgroundSize: "cover",
              }}
            >
              <div
                className="absolute inset-0 z-0"
                style={{ backgroundColor: card_background_color }}
              />
              <div className="relative z-[1] flex size-full flex-col items-start justify-between gap-8">
                <div className="flex w-full flex-col gap-2">
                  <p className="flex flex-col items-start text-[17px]/[31px] font-bold">
                    {headline}
                    {isSpecialisation && (
                      <span
                        className="flex text-2xl font-bold"
                        style={{ color: ACCENT }}
                      >
                        {card.meta_title}
                      </span>
                    )}
                  </p>
                  <p className="text-xs font-bold">{card.meta_description}</p>
                  {card.meta_duration && (
                    <div className="mt-2 w-fit rounded bg-[#FAFAFA80] p-1.5 text-sm text-[#222023]">
                      <b>Duration: </b>
                      <span>{card.meta_duration}</span>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="flex h-7 items-center justify-center gap-2 whitespace-nowrap rounded-md py-2 pl-0 pr-4 text-base font-bold transition-colors"
                >
                  <span>{cta_text}</span>
                  <KnowMoreArrow />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProgramCards1045;
